/* Copyright (c) Trimble Inc. All rights reserved. */

import { createContext, useContext } from 'react';
import { type AuthConfig, type Environment } from '../models/AuthConfig.model';

/**
 * Authentication Context State
 */
export interface AuthContextState {
  // Configuration
  isAvailable?: boolean;
  clientId?: string;
  environment?: Environment;
  scopes?: readonly string[];
  authConfig?: AuthConfig;

  // State flags
  isConfigured: boolean;

  // Methods
  configure: (config: {
    clientId: string;
    environment: Environment;
    scopes: readonly string[];
  }) => void;
  getAuthConfig: () => AuthConfig | null;
  clear: () => void;
}

/**
 * Authentication Context
 */
export const AuthContext = createContext<AuthContextState | null>(null);

/**
 * Hook to use authentication context
 */
export const useAuth = (): AuthContextState => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Hook to get auth configuration (safe version that doesn't throw)
 */
export const useAuthConfig = (): AuthContextState => {
  const context = useContext(AuthContext);
  return {
    isAvailable: !!context,
    isConfigured: context?.isConfigured ?? false,
    authConfig: context?.authConfig ?? undefined,
    clientId: context?.clientId,
    environment: context?.environment,
    scopes: context?.scopes,
    configure:
      context?.configure ??
      ((): void => {
        // No-op when context unavailable
      }),
    getAuthConfig: context?.getAuthConfig ?? ((): null => null),
    clear:
      context?.clear ??
      ((): void => {
        // No-op when context unavailable
      }),
  };
};
